import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  ReportableEngagement,
  StatusReportAiSummaryResult,
  StatusReportConfigInput,
  StatusReportHideTicketInput,
  StatusReportHighlightInput,
  StatusReportOrderNumberInput,
  StatusReportOverrideInput,
  StatusReportTicketNoteInput,
  StatusReportResponse,
  StatusReportSendResult,
} from "@msc/shared";

import { api } from "../lib/api.js";

interface EngagementsResponse {
  items: ReportableEngagement[];
}

/** Beauftragungen, für die die eigene Person einen Bericht sehen darf. */
export function useReportableEngagements() {
  return useQuery<ReportableEngagement[]>({
    queryKey: ["status-reports", "engagements"],
    queryFn: async () => {
      const res = await api.get<EngagementsResponse>(
        "/status-reports/engagements",
      );
      return res.items;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Der zusammengestellte Bericht. `month` als `YYYY-MM`. */
/**
 * Die Schluessel des Statusberichts an EINER Stelle — Abfrage, Invalidierung
 * und Test lesen dieselbe Quelle. Vorher standen sie dreimal als Literal da,
 * und genau dabei ist der Monat an eine Stelle geraten, an die er nicht
 * gehoerte (s. `useInvalidateEngagementReports`).
 */
const BERICHTE = "status-reports";

/** Ein Bericht: Beauftragung x Monat. */
export function berichtsSchluessel(
  engagementId: string | null,
  month: string | null,
) {
  return [BERICHTE, engagementId, month] as const;
}

/** Alle Berichte einer Beauftragung, ueber jeden Monat hinweg. */
export function beauftragungsSchluessel(engagementId: string | null) {
  return [BERICHTE, engagementId] as const;
}

export function useStatusReport(
  engagementId: string | null,
  month: string | null,
) {
  return useQuery<StatusReportResponse>({
    queryKey: berichtsSchluessel(engagementId, month),
    enabled: Boolean(engagementId && month),
    queryFn: () =>
      api.get<StatusReportResponse>(
        `/status-reports/${engagementId}?month=${month}`,
      ),
  });
}

/**
 * Monatsgebundene Schreibpfade invalidieren denselben Bericht — er wird
 * serverseitig neu zusammengestellt, damit Budget-Auswahl und Ticketzeilen nie
 * auseinanderlaufen.
 *
 * **Nur den einen Monat.** Hervorhebungen, Handzeilen, Notizen und der
 * Versandvermerk haengen an (Beauftragung x Monat); die uebrigen Monate im
 * Zwischenspeicher sind davon nicht beruehrt.
 */
function useInvalidateReport(engagementId: string | null, month: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({
      queryKey: berichtsSchluessel(engagementId, month),
    });
  };
}

/**
 * Die Einstellungen haengen an der **Beauftragung**, nicht am Monat
 * (`status_report_config`, Primaerschluessel `engagement_id`). Wer sie aendert,
 * aendert damit JEDEN Monat dieser Beauftragung — also muss auch jeder Monat
 * aus dem Zwischenspeicher fliegen.
 *
 * **Der Fehler, den das behebt (05.10.2026).** Invalidiert wurde nur der gerade
 * gezeigte Monat. Jeder andere Monat, der schon einmal geladen war, lag weiter
 * mit dem Stand VOR dem Speichern im Zwischenspeicher, und weil die App
 * `staleTime: 30_000` setzt (s. `main.tsx`), wurde er innerhalb dieser halben
 * Minute ohne Nachfrage beim Server wieder angezeigt. Im Folgemonat standen
 * dann keine Empfaenger, keine Anrede und wieder die automatische
 * Budget-Auswahl — gespeichert war alles richtig, gezeigt wurde Altes.
 *
 * Der Befund erklaert vermutlich auch die unaufgeklaerte Rueckmeldung vom
 * 08.09.2026, dass eine geleerte Budget-Auswahl „nicht greife": Die Pruefung
 * in der Datenbank war damals gruen, die Anzeige kam aus demselben
 * Zwischenspeicher.
 *
 * React Query vergleicht Schluessel ueber ihren Anfang, deshalb trifft der
 * Schluessel ohne Monat alle Monate dieser Beauftragung.
 */
function useInvalidateEngagementReports(engagementId: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({
      queryKey: beauftragungsSchluessel(engagementId),
    });
  };
}

/**
 * Einstellungen speichern. **Ohne Monat**, und das ist keine Nachlaessigkeit:
 * Es gibt sie je Beauftragung nur einmal (s. `useInvalidateEngagementReports`).
 */
export function useUpdateReportConfig(engagementId: string | null) {
  const invalidate = useInvalidateEngagementReports(engagementId);
  return useMutation({
    mutationFn: (payload: StatusReportConfigInput) =>
      api.put(`/status-reports/${engagementId}/config`, payload),
    onSuccess: invalidate,
  });
}

/**
 * Einen Vorgang im Kundenbericht ausblenden oder wieder einblenden.
 *
 * **Ohne Monat**, und deshalb ueber die ganze Beauftragung invalidiert: Die
 * Entscheidung gilt fuer jeden Monat (s. `useInvalidateEngagementReports`).
 */
export function useSetTicketHidden(engagementId: string | null) {
  const invalidate = useInvalidateEngagementReports(engagementId);
  return useMutation({
    mutationFn: (payload: StatusReportHideTicketInput) =>
      api.put<{ hidden: boolean }>(
        `/status-reports/${engagementId}/ticket-hidden`,
        payload,
      ),
    onSuccess: invalidate,
  });
}

/**
 * Bestellnummer eines Budgets. **Ohne Monat**, deshalb ueber die ganze
 * Beauftragung invalidiert: Sie gilt fuer jeden Bericht.
 */
export function useSetBudgetOrderNumber(engagementId: string | null) {
  const invalidate = useInvalidateEngagementReports(engagementId);
  return useMutation({
    mutationFn: (payload: StatusReportOrderNumberInput) =>
      api.put<{ orderNumber: string | null }>(
        `/status-reports/${engagementId}/order-number`,
        payload,
      ),
    onSuccess: invalidate,
  });
}

export function useAddHighlight(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (payload: StatusReportHighlightInput) =>
      api.post<{ id: string }>(
        `/status-reports/${engagementId}/highlights?month=${month}`,
        payload,
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveHighlight(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (highlightId: string) =>
      api.delete(`/status-reports/highlights/${highlightId}`),
    onSuccess: invalidate,
  });
}

export function useAddOverride(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (payload: StatusReportOverrideInput) =>
      api.post<{ id: string }>(
        `/status-reports/${engagementId}/overrides?month=${month}`,
        payload,
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveOverride(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (overrideId: string) =>
      api.delete(`/status-reports/overrides/${overrideId}`),
    onSuccess: invalidate,
  });
}

/**
 * Setzt oder entfernt „Status und nächste Schritte" für einen Vorgang.
 *
 * PUT statt POST: Je Vorgang und Bericht gibt es genau eine Notiz, zweimal
 * dasselbe zu schicken ändert nichts. Leerer Text entfernt sie.
 */
export function useSetTicketNote(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (payload: StatusReportTicketNoteInput) =>
      api.put<{ nextSteps: string | null }>(
        `/status-reports/${engagementId}/ticket-note?month=${month}`,
        payload,
      ),
    onSuccess: invalidate,
  });
}

export function useGenerateAiSummaries(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: () =>
      api.post<StatusReportAiSummaryResult>(
        `/status-reports/${engagementId}/ai-summaries?month=${month}`,
        {},
      ),
    onSuccess: invalidate,
  });
}

export function useSaveEditedReport(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: (html: string) =>
      api.post(`/status-reports/${engagementId}/edited?month=${month}`, {
        html,
      }),
    onSuccess: invalidate,
  });
}

export function useResetEditedReport(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: () =>
      api.delete(`/status-reports/${engagementId}/edited?month=${month}`),
    onSuccess: invalidate,
  });
}

export function useMarkReportSent(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: () =>
      api.post<StatusReportSendResult>(
        `/status-reports/${engagementId}/send?month=${month}`,
        {},
      ),
    onSuccess: invalidate,
  });
}

/**
 * „Versendet" zurücknehmen (Befund B8).
 *
 * Derselbe Pfad wie beim Setzen, nur mit `DELETE` — der Rückweg ist die
 * Umkehrung derselben Aktion, keine eigene Sache.
 */
export function useRevertReportSent(
  engagementId: string | null,
  month: string | null,
) {
  const invalidate = useInvalidateReport(engagementId, month);
  return useMutation({
    mutationFn: () =>
      api.delete<StatusReportSendResult>(
        `/status-reports/${engagementId}/send?month=${month}`,
      ),
    onSuccess: invalidate,
  });
}
